import { useCallback, useEffect, useState } from 'react';
import { FirstPartyDrawdownType } from 'components/Molecules/Drawdowns/FirstPartyCreateDrawdownDrawer/formData';
import { PurposeOption } from 'components/Molecules/Payments/CreatePaymentDrawer/PaymentPurposeDropdown/PaymentPurposeDropdown';
import { TSpotFXRequestForm } from 'components/Molecules/Trade/CreateSpotDrawer/formData';
import { FormikProps } from 'formik';
import { debounce } from 'lodash';
import { useQuery } from 'react-query';

import { PaymentPurposeDto } from '@alpha/payments-dtos';

import useAlphaSnackbar from '../../../../../../hooks/useAlphaSnackbar';
import useLog from '../../../../../../hooks/useLog';
import PaymentsService from '../../../../../../services/Payments/payments.service';
import t from '../../../../../../utils/translationHelper';

interface UsePurposeOfPaymentsProps {
  beneficiaryBankCountryCode?: string;
  beneficiaryCurrencyCode?: string;
  amount?: number;
  paymentForm?: FormikProps<FirstPartyDrawdownType | FirstPartyDrawdownType> | FormikProps<TSpotFXRequestForm>;
}

const usePurposeOfPayments = ({
  beneficiaryBankCountryCode,
  beneficiaryCurrencyCode,
  amount,
  paymentForm,
}: UsePurposeOfPaymentsProps) => {
  const [purpose, setPurpose] = useState<string | undefined>(undefined);
  const [purposeType, setPurposeType] = useState<string | undefined>(undefined);
  const [showInvoiceFields, setShowInvoiceFields] = useState<boolean>(false);
  const sb = useAlphaSnackbar();
  const { logError } = useLog();

  const paymentPurposesQuery = useQuery(
    ['getPaymentPurpose', beneficiaryBankCountryCode, beneficiaryCurrencyCode, amount],
    () => PaymentsService.getPaymentPurpose(
      beneficiaryBankCountryCode || '',
      beneficiaryCurrencyCode || '',
      amount?.toString() || '',
    ),
    {
      enabled: false,
      onError: () => {
        sb.trigger(t('could_not_load_payment_purposes'), 'error');
        logError({ action: 'Could not load payment purposes' });
      },
    },
  );

  const canBeFreeText = paymentPurposesQuery.data?.canBeFreeText ?? true;
  const required = paymentPurposesQuery.data?.required ?? false;

  const debouncedRefetch = useCallback(
    debounce(() => {
      if (amount && beneficiaryBankCountryCode && beneficiaryCurrencyCode) {
        paymentPurposesQuery.refetch();
      }
    }, 800),
    [amount, beneficiaryBankCountryCode, beneficiaryCurrencyCode],
  );

  useEffect(() => {
    debouncedRefetch();
  }, [amount, beneficiaryBankCountryCode, beneficiaryCurrencyCode]);

  useEffect(() => {
    // Reset purpose if canBeFreeText changes
    if (canBeFreeText !== undefined) {
      setPurpose(undefined);
    }
  }, [canBeFreeText]);

  useEffect(() => {
    paymentPurposesQuery.remove();
  }, []);

  const handlePurposeChange = (event: any, value: PurposeOption) => {
    const selectedValue = value;
    setPurpose(selectedValue?.value);

    if (selectedValue?.type === 'Service') {
      paymentForm?.setFieldValue('invoiceDate', paymentForm?.values.valueDate, false);
      paymentForm?.setFieldValue('purposeType', 'Service', false);
      setShowInvoiceFields(true);
      setPurposeType('Service');
    } else {
      paymentForm?.setFieldValue('invoiceDate', null, false);
      paymentForm?.setFieldValue('purposeType', selectedValue?.type, false);
      setShowInvoiceFields(false);
      setPurposeType(selectedValue?.type);
    }
  };

  const purposeOfPaymentValidated = (
    isRequired: boolean,
    paymentPurpose?: string | PaymentPurposeDto,
    shouldShowInvoiceFields?: boolean,
    invoiceNumber?:string, invoiceDate?:string,
  ) => {
    if (!isRequired) {
      return true;
    }
    if (!shouldShowInvoiceFields) {
      return !!(isRequired && paymentPurpose);
    }
    return !!(isRequired && paymentPurpose && invoiceNumber && invoiceDate);
  };

  return {
    paymentPurposesQuery,
    purpose,
    purposeType,
    showInvoiceFields,
    canBeFreeText,
    required,
    handlePurposeChange,
    setPurpose,
    setPurposeType,
    setShowInvoiceFields,
    loading: paymentPurposesQuery.isLoading,
    purposes: paymentPurposesQuery.data?.purposes,
    purposeOfPaymentValidated,
  };
};

export default usePurposeOfPayments;
